import { useEffect, useState } from 'preact/hooks';
import type { Connection } from '../../types';
import { connectionManagementService } from '../../services/connections/connection-management.service';
import { MainLayout } from '../../components/MainLayout';
import { useScopeContext } from '../../contexts/ScopeContext';
import { ConnectionHeader } from '../../features/connections/components/ConnectionHeader';
import { CredentialsTab } from '../../features/connections/components/CredentialsTab';
import { TestTab } from '../../features/connections/components/TestTab';
import { PermissionsTab } from '../../features/connections/components/PermissionsTab';
import { KnowledgeTab } from '../../features/connections/components/KnowledgeTab';
import { InfoTab } from '../../features/connections/components/InfoTab';

type TabType = 'credentials' | 'test' | 'permissions' | 'knowledge' | 'info';

export function ConnectionDetailsPage({ connectionId }: { connectionId: string }) {
  const scope = useScopeContext();
  const [connection, setConnection] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('credentials');

  useEffect(() => {
    loadConnection();
  }, [connectionId]);

  const loadConnection = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[ConnectionDetailsPage] Loading connection:', { teamId: scope.teamId, connectionId });
      const connection = await connectionManagementService.getConnection(scope.teamId, connectionId);
      console.log('[ConnectionDetailsPage] Loaded connection:', connection);
      setConnection(connection);
    } catch (err) {
      console.error('[ConnectionDetailsPage] Error loading connection:', err);
      setError(err instanceof Error ? err.message : 'Failed to load connection');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !connection) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="container mx-auto p-4">
            <div className="alert alert-error">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="font-bold">{error || 'Connection not found'}</div>
                <div className="text-sm mt-1 opacity-80">
                  Team ID: {scope.teamId}
                  <br />
                  Connection ID: {connectionId || 'undefined'}
                </div>
              </div>
            </div>
            <a href={`${scope.getNavigationPrefix()}/connections`} className="btn btn-primary mt-4">
              Back to Team Connections
            </a>
          </div>
        </div>
      </MainLayout>
    );
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'credentials':
        return <CredentialsTab connection={connection} onConnectionUpdate={loadConnection} />;
      case 'test':
        return <TestTab connection={connection} onConnectionUpdate={loadConnection} />;
      case 'permissions':
        return <PermissionsTab connection={connection} onConnectionUpdate={loadConnection} />;
      case 'knowledge':
        return <KnowledgeTab connection={connection} onConnectionUpdate={loadConnection} />;
      case 'info':
        return <InfoTab connection={connection} onConnectionUpdate={loadConnection} />;
      default:
        return null;
    }
  };

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
      <div className="container mx-auto p-4 max-w-4xl">
        <ConnectionHeader connection={connection} scope={scope} />

        {/* Tabs */}
        <div className="tabs tabs-boxed mb-6">
          <button
            className={`tab ${activeTab === 'credentials' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('credentials')}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
            </svg>
            Credentials
          </button>
          <button
            className={`tab ${activeTab === 'test' ? 'tab-active' : ''} ${!connection || !connection.hasCredentials ? 'tab-disabled' : ''}`}
            onClick={() => connection && connection.hasCredentials && setActiveTab('test')}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Test
            {(!connection || !connection.hasCredentials) && (
              <span className="badge badge-xs badge-ghost ml-1">Save credentials first</span>
            )}
          </button>
          <button
            className={`tab ${activeTab === 'permissions' ? 'tab-active' : ''} ${!connection?.active ? 'tab-disabled' : ''}`}
            onClick={() => connection?.active && setActiveTab('permissions')}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
            </svg>
            Permissions
            {!connection?.active && (
              <span className="badge badge-xs badge-ghost ml-1">Activate first</span>
            )}
          </button>
          <button
            className={`tab ${activeTab === 'knowledge' ? 'tab-active' : ''} ${!connection?.active ? 'tab-disabled' : ''}`}
            onClick={() => connection?.active && setActiveTab('knowledge')}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
            </svg>
            Knowledge
            {!connection?.active && (
              <span className="badge badge-xs badge-ghost ml-1">Activate first</span>
            )}
          </button>
          <button
            className={`tab ${activeTab === 'info' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Info
          </button>
        </div>

        {/* Tab Content */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <div className="min-h-[400px]">
              {renderActiveTab()}
            </div>
          </div>
        </div>
      </div>
      </div>
    </MainLayout>
  );
}
